import React, { useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaShieldAlt, FaLock, FaUserCheck } from 'react-icons/fa';
import { Helmet } from 'react-helmet-async';

const Privacy = () => {
  const { t } = useTranslation();
  const privacyRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (privacyRef.current) observer.observe(privacyRef.current);

    return () => observer.disconnect();
  }, []);

  return (
    <>
    <Helmet>
      <title>Политика конфиденциальности — Mileage Riot</title>
      <meta name="description" content="Политика конфиденциальности Mileage Riot — защита ваших данных." />
      <link rel="canonical" href="https://mileageriot.com/privacy" />
      <meta property="og:title" content="Политика конфиденциальности — Mileage Riot" />
      <meta property="og:url" content="https://mileageriot.com/privacy" />
      <meta property="og:locale" content="ru_RU" />
    </Helmet>
    <section className="privacy-modern page-privacy" ref={privacyRef}>
      <div className="container">
        {/* Section Header */}
        <div className="section-header text-center mb-5 fade-in-up stagger-1">
          <div className="section-badge">
            <FaShieldAlt className="me-2" />
            <span>{t('privacy.badge') || 'Конфиденциальность'}</span>
          </div>
          <h2 className="section-title text-glow text-white">
            {t('privacy.title') || 'Политика конфиденциальности'}
          </h2>
          <div className="section-decoration mx-auto"></div>
        </div>

        {/* Privacy Content */}
        <div className="privacy-content">
          <div className="privacy-section fade-in-up stagger-2">
            <h3 className="privacy-section-title">
              <FaLock className="privacy-icon text-primary me-3" />
              {t('privacy.executiveSummary.title')}
            </h3>
            <div className="privacy-divider"></div>
            <p className="privacy-text">
              {t('privacy.executiveSummary.content')}
            </p>
          </div>

          <div className="privacy-section fade-in-up stagger-3">
            <h3 className="privacy-section-title">
              <FaUserCheck className="privacy-icon text-primary me-3" />
              {t('privacy.evaluation.title')}
            </h3>
            <div className="privacy-divider"></div>
            
            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.evaluation.compliance.title')}</h4>
              <ul className="privacy-list">
                {t('privacy.evaluation.compliance.points', { returnObjects: true }).map((point, index) => (
                  <li key={index} className="privacy-list-item">{point}</li>
                ))}
              </ul>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.evaluation.informationCollection.title')}</h4>
              <ul className="privacy-list">
                {t('privacy.evaluation.informationCollection.points', { returnObjects: true }).map((point, index) => (
                  <li key={index} className="privacy-list-item">{point}</li>
                ))}
              </ul>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.evaluation.logData.title')}</h4>
              <ul className="privacy-list">
                {t('privacy.evaluation.logData.points', { returnObjects: true }).map((point, index) => (
                  <li key={index} className="privacy-list-item">{point}</li>
                ))}
              </ul>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.evaluation.serviceProviders.title')}</h4>
              <ul className="privacy-list">
                {t('privacy.evaluation.serviceProviders.points', { returnObjects: true }).map((point, index) => (
                  <li key={index} className="privacy-list-item">{point}</li>
                ))}
              </ul>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.evaluation.externalLinks.title')}</h4>
              <ul className="privacy-list">
                {t('privacy.evaluation.externalLinks.points', { returnObjects: true }).map((point, index) => (
                  <li key={index} className="privacy-list-item">{point}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="privacy-section fade-in-up stagger-4">
            <h3 className="privacy-section-title">
              <FaShieldAlt className="privacy-icon text-primary me-3" />
              {t('privacy.recommendations.title')}
            </h3>
            <div className="privacy-divider"></div>
            <ol className="privacy-list privacy-numbered-list">
              {t('privacy.recommendations.points', { returnObjects: true }).map((point, index) => (
                <li key={index} className="privacy-list-item">{point}</li>
              ))}
            </ol>
          </div>

          <div className="privacy-section fade-in-up stagger-5">
            <h3 className="privacy-section-title">
              <FaLock className="privacy-icon text-primary me-3" />
              {t('privacy.conclusion.title')}
            </h3>
            <div className="privacy-divider"></div>
            <p className="privacy-text">{t('privacy.conclusion.content')}</p>
            <p className="privacy-notice">{t('privacy.notice')}</p>
          </div>

          <div className="privacy-section fade-in-up stagger-6">
            <h3 className="privacy-section-title">
              <FaUserCheck className="privacy-icon text-primary me-3" />
              {t('privacy.policy.title')}
            </h3>
            <div className="privacy-divider"></div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.policy.introduction.title')}</h4>
              <p className="privacy-text">{t('privacy.policy.introduction.content')}</p>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.policy.dataCollection.title')}</h4>
              <p className="privacy-text">{t('privacy.policy.dataCollection.content')}</p>
              <ul className="privacy-list">
                {t('privacy.policy.dataCollection.points', { returnObjects: true }).map((point, index) => (
                  <li key={index} className="privacy-list-item">{point}</li>
                ))}
              </ul>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.policy.dataUse.title')}</h4>
              <p className="privacy-text">{t('privacy.policy.dataUse.content')}</p>
              <ul className="privacy-list">
                {t('privacy.policy.dataUse.points', { returnObjects: true }).map((point, index) => (
                  <li key={index} className="privacy-list-item">{point}</li>
                ))}
              </ul>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.policy.dataRetention.title')}</h4>
              <p className="privacy-text">{t('privacy.policy.dataRetention.content')}</p>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.policy.cookies.title')}</h4>
              <p className="privacy-text">{t('privacy.policy.cookies.content')}</p>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.policy.gameTracking.title')}</h4>
              <p className="privacy-text">{t('privacy.policy.gameTracking.content')}</p>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.policy.security.title')}</h4>
              <p className="privacy-text">{t('privacy.policy.security.content')}</p>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.policy.gdprRights.title')}</h4>
              <ul className="privacy-list">
                {t('privacy.policy.gdprRights.points', { returnObjects: true }).map((point, index) => (
                  <li key={index} className="privacy-list-item">{point}</li>
                ))}
              </ul>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.policy.exercisingRights.title')}</h4>
              <p className="privacy-text">{t('privacy.policy.exercisingRights.content')}</p>
              <div className="contact-info">
                <p>Email: <a href={`mailto:${t('privacy.policy.exercisingRights.email')}`} className="contact-link">
                  {t('privacy.policy.exercisingRights.email')}
                </a></p>
                <p>{t('privacy.policy.exercisingRights.response')}</p>
              </div>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.policy.childrenPrivacy.title')}</h4>
              <p className="privacy-text">{t('privacy.policy.childrenPrivacy.content')}</p>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.policy.changes.title')}</h4>
              <p className="privacy-text">{t('privacy.policy.changes.content')}</p>
            </div>

            <div className="privacy-subsection">
              <h4 className="privacy-subsection-title">{t('privacy.policy.contact.title')}</h4>
              <p className="privacy-text">
                {t('privacy.policy.contact.content')}
                <a href={`mailto:${t('privacy.policy.exercisingRights.email')}`} className="contact-link">
                  {t('privacy.policy.exercisingRights.email')}
                </a>
              </p>
            </div>

            <div className="effective-date">
              <p className="privacy-text">{t('privacy.policy.effectiveDate')}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Background decoration */}
      <div className="privacy-bg-decoration"></div>
    </section>
    </>
  );
};

export default Privacy;